import { useState } from "react";
import { Check, Clock, PartyPopper, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { EmptyState } from "@/components/common";
import { Celebration, KIDS_RGB, useBusy } from "@/components/kids/kidsUi";

function KidChore({ c, hubId, onChanged, onCelebrate }) {
  const [busy, run] = useBusy();
  const act = (path, after) => run(path, async () => {
    try {
      await api.post(`/organizations/${hubId}/chore-instances/${c.id}/${path}`);
      after?.();
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  });
  const waiting = c.status === "waiting";
  const approved = c.status === "approved";
  return (
    <li className="flex flex-col gap-4 rounded-2xl border-2 bg-q-surface p-5 sm:flex-row sm:items-center" data-testid={`kid-chore-${c.id}`} data-status={c.status}
      style={{ borderColor: waiting ? `rgba(${KIDS_RGB},0.5)` : "rgb(var(--q-border))" }}>
      <span className="inline-flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl text-4xl" style={{ background: `rgba(${KIDS_RGB},0.12)` }}>{c.icon}</span>
      <div className="min-w-0 flex-1">
        <p className="font-display text-xl font-extrabold text-q-text" data-testid={`kid-chore-title-${c.id}`}>{c.title}</p>
        <p className="text-base font-bold" style={{ color: "rgb(var(--glow-money))" }}>{money(c.reward)}</p>
        {c.note && <p className="text-sm text-q-muted">{c.note}</p>}
        {c.reject_note && c.status === "todo" && <p className="mt-1 text-sm font-semibold text-q-accent" data-testid={`kid-chore-try-again-${c.id}`}>Try again: {c.reject_note}</p>}
      </div>
      {c.status === "todo" && (
        <button className="q-btn min-h-[56px] justify-center rounded-2xl px-8 text-lg font-extrabold text-white" style={{ background: `rgb(${KIDS_RGB})` }} disabled={!!busy}
          onClick={() => act("done", onCelebrate)} data-testid={`kid-chore-done-btn-${c.id}`}><Check className="h-6 w-6" strokeWidth={3} /> Done!</button>
      )}
      {waiting && (
        <div className="flex flex-col items-stretch gap-2 sm:items-end">
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-q-text" data-testid={`kid-chore-waiting-${c.id}`}><Clock className="h-4 w-4 text-q-accent" /> Waiting for a grown-up</span>
          <button className="q-btn q-btn-secondary min-h-[44px] justify-center" disabled={!!busy} onClick={() => act("undo-done")} data-testid={`kid-chore-oops-btn-${c.id}`}><Undo2 className="h-4 w-4" /> Oops, not done</button>
        </div>
      )}
      {approved && <span className="inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: "rgb(var(--glow-money))" }} data-testid={`kid-chore-approved-${c.id}`}><PartyPopper className="h-4 w-4" /> Approved!</span>}
      {c.status === "missed" && <span className="text-sm font-bold text-q-faint">Missed</span>}
    </li>
  );
}

export default function KidChores({ data, hubId, onChanged }) {
  const [party, setParty] = useState(false);
  const list = data.instances;
  const left = list.filter((c) => c.status === "todo").length;
  return (
    <div data-testid="kid-chores">
      <p className="mb-6 text-lg font-semibold text-q-muted q-onwall" data-testid="kid-chores-left">{left ? `${left} chore${left > 1 ? "s" : ""} left today` : "All done for today!"}</p>
      {list.length === 0 ? <EmptyState Icon={PartyPopper} title="No chores today" hint="Enjoy your free time!" rgb={KIDS_RGB} testid="kid-chores-empty" /> : (
        <ul className="space-y-3">{list.map((c) => <KidChore key={c.id} c={c} hubId={hubId} onChanged={onChanged} onCelebrate={() => setParty(true)} />)}</ul>
      )}
      {party && <Celebration emoji="🎉" text="Great job! A grown-up will check it." onDone={() => setParty(false)} />}
    </div>
  );
}
